import { conditionalRender } from '@repo/toolkit/conditional-render'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getTransportMeans } from '../../../api/transport-means/get-transport-means'
import { getTripAddresses } from '../../../api/trip-addresses/get-trip-addresses'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../lib/types/enums/mission/mission-type.enum'
import {
  TransportTypeEnum,
  transportTypeMapping
} from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

interface Props {
  filter?: TripStepTypes.Filter
  onFilterChange: (filter: TripStepTypes.Filter) => void
}

export function TripStepsFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof TripStepTypes.Filter>(
    key: K,
    value: TripStepTypes.Filter[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: tripAddresses } = useQuery({
    queryKey: ['trip-addresses'],
    queryFn: () => getTripAddresses()
  })

  const { data: transportMeans } = useQuery({
    queryKey: ['transport-means'],
    queryFn: () => getTransportMeans()
  })

  return (
    <div className={'flex flex-col gap-4 mt-2'}>
      <DateStringInput
        label={'Date du trajet'}
        value={filter?.departureDate}
        onChange={(date) => setFilter('departureDate', date)}
      />
      <SelectInput
        label={'Type de transport'}
        options={transportMeans?.data?.map((transportMean) => ({
          label: getTranslatedName(transportMean),
          value: getTranslatedName(transportMean).toLowerCase()
        }))}
        onChange={(transportMean) => setFilter('transportMean', transportMean)}
        selectedOption={conditionalRender(!!filter?.transportMean, {
          label:
            getTranslatedName(
              transportMeans?.data?.find(
                (tm) =>
                  getTranslatedName(tm).toLowerCase() === filter?.transportMean?.toLowerCase()
              )
            ) ||
            filter?.transportMean ||
            '',
          value: filter?.transportMean
        })}
      />
      <MultiSelectInput
        variant="primary"
        label={'Type de mission'}
        options={Object.values(MissionTypeEnum).map((type) => ({
          label: missionTypeEnumMapping[type].label,
          value: type
        }))}
        onChange={(type) => setFilter('missionType', type)}
        selectedOptions={
          filter?.missionType?.map((type) => ({
            label: missionTypeEnumMapping[type].label,
            value: type
          })) ?? []
        }
      />
      <SelectInput
        label={'Type de trajet'}
        options={Object.values(TransportTypeEnum).map((type) => ({
          label: transportTypeMapping[type].label,
          value: type
        }))}
        onChange={(type) => setFilter('transportType', type)}
        selectedOption={conditionalRender(
          !!filter?.transportType && !!transportTypeMapping[filter.transportType],
          {
            label: transportTypeMapping[filter!.transportType!]?.label || '',
            value: filter!.transportType!
          }
        )}
      />
      <MultiSelectInput
        searchable
        variant={'primary'}
        label={'Lieu de rendez-vous'}
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: Number(tripAddress.id)
        }))}
        onChange={(selectedOptions) => setFilter('meetingPlaceIds', selectedOptions)}
        selectedOptions={filter?.meetingPlaceIds?.map((id) => ({
          label:
            tripAddresses?.data.find((tripAddress) => Number(tripAddress.id) === id)?.label ?? '',
          value: id
        }))}
      />
      <MultiSelectInput
        label={'Ville (Départ / Arrivée)'}
        variant={'primary'}
        searchable
        options={tripAddresses?.data.map((tripAddress) => ({
          label: tripAddress.label,
          value: Number(tripAddress.id)
        }))}
        onChange={(selectedOptions) => setFilter('placeIds', selectedOptions)}
        selectedOptions={filter?.placeIds?.map((id) => ({
          label:
            tripAddresses?.data.find((tripAddress) => Number(tripAddress.id) === id)?.label ?? '',
          value: id
        }))}
      />
    </div>
  )
}
